// Web push (FCM) — how the shopper learns that a saved order can be confirmed.
//
// The offline flow is: checkout is saved as `pending_confirmation` → the
// gateway re-checks availability when the outlet returns → THIS is the moment
// the shopper has to be told. Without a registered device token the gateway's
// FcmSender has nowhere to deliver, so the shopper would only discover the
// order by revisiting /orders.
//
// Everything here is opt-in and fails soft: an unconfigured deployment (no
// VAPID key) or an unsupported browser must never break the shop.
import firebase from 'firebase/compat/app';
import 'firebase/compat/messaging';
import { api } from './api.js';
import { firebaseApp } from './firebase.js';

const VAPID_KEY = (import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined) || '';
const SENDER_ID = (import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string | undefined) || '';
const SW_URL = '/push-sw.js';
const TOKEN_KEY = 'mallFcmToken';

export type PushResult =
  | 'granted'      // registered (or already was)
  | 'default'      // the prompt was dismissed
  | 'denied'       // the shopper blocked notifications
  | 'unsupported'  // no Notification/Push support on this browser
  | 'skipped'      // deployment has no VAPID/messaging sender id
  | 'error';

/** True when this deployment can send pushes at all. */
export function pushConfigured(): boolean {
  return Boolean(VAPID_KEY && SENDER_ID);
}

/** The browser's current permission, or 'unsupported'. */
export function pushPermission(): NotificationPermission | 'unsupported' {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

function messaging(): firebase.messaging.Messaging | null {
  try {
    return firebase.messaging(firebaseApp());
  } catch {
    return null;
  }
}

async function registerDeviceToken(): Promise<PushResult> {
  const m = messaging();
  if (!m) return 'unsupported';
  try {
    if (!(await firebase.messaging.isSupported())) return 'unsupported';
  } catch {
    return 'unsupported';
  }

  if (Notification.permission !== 'granted') {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'default';
  }

  try {
    const reg = await navigator.serviceWorker.register(SW_URL, { scope: '/' });
    await navigator.serviceWorker.ready;
    const token = await m.getToken({ vapidKey: VAPID_KEY, serviceWorkerRegistration: reg });
    if (!token) return 'error';
    window.localStorage.setItem(TOKEN_KEY, token);
    await api.registerDevice(token);
    return 'granted';
  } catch (err) {
    // A missing VAPID key or a blocked origin lands here — never fatal.
    console.warn('[push] device registration failed', err);
    return 'error';
  }
}

/** The FCM token this browser last registered with the gateway, if any. */
export function currentToken(): string {
  try {
    return window.localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

/**
 * Ask for notification permission (when still undecided) and register this
 * browser with the gateway. Safe to call repeatedly.
 *
 * `silent` skips the permission prompt: used on sign-in, where prompting out of
 * nowhere is rude — the shopper is asked from the order page instead, at the
 * point where a notification is actually useful.
 */
export async function enablePush({ silent = false }: { silent?: boolean } = {}): Promise<PushResult> {
  if (!pushConfigured()) return 'skipped';
  if (typeof Notification === 'undefined' || !('serviceWorker' in navigator)) return 'unsupported';
  if (silent && Notification.permission !== 'granted') return pushPermission() as PushResult;
  return registerDeviceToken();
}

/** Forget this browser: the server stops pushing, the token is revoked. */
export async function disablePush(): Promise<void> {
  const token = currentToken();
  if (token) {
    try {
      await api.unregisterDevice(token);
    } catch {
      /* the session may already be gone — the row is pruned anyway */
    }
  }
  try {
    window.localStorage.removeItem(TOKEN_KEY);
    const m = messaging();
    if (m && pushConfigured()) await m.deleteToken();
  } catch {
    /* ignore */
  }
}

export interface PushMessage {
  title: string;
  body: string;
  url: string;
  orderId: string;
  type: string;
}

/**
 * Foreground notifications: FCM does not raise a system notification while the
 * page is focused, so the app turns them into an in-app toast instead.
 */
export function onPushMessage(cb: (msg: PushMessage) => void): () => void {
  if (!pushConfigured()) return () => undefined;
  const m = messaging();
  if (!m) return () => undefined;
  try {
    return m.onMessage((payload: firebase.messaging.MessagePayload) => {
      const note = payload.notification || {};
      const data = (payload.data || {}) as Record<string, string>;
      cb({
        title: note.title || 'Online Mall',
        body: note.body || '',
        url: data.url || '/orders',
        orderId: data.orderId || '',
        type: data.type || '',
      });
    });
  } catch {
    return () => undefined;
  }
}
